import { resolveThemeToken } from '../../themeTokens';
import { benefitHeadline, type CreatorDraftPayload } from './draftSchema';

type Props = {
  payload: CreatorDraftPayload;
  storeName?: string;
};

const formatPrice = (paise: number | undefined): string => {
  if (paise == null) return '—';
  if (paise === 0) return 'Free';
  return `₹${Math.round(paise / 100).toLocaleString('en-IN')}`;
};

const formatValidity = (days: number | undefined): string => {
  if (days == null) return '—';
  if (days <= 0) return 'Lifetime';
  if (days % 365 === 0) return `${days / 365} yr`;
  if (days % 30 === 0) return `${days / 30} mo`;
  return `${days} days`;
};

/**
 * Mirrors the mobile MembershipOfferingCard (the card a customer sees before
 * joining): dark surface, a tier-accent top line, the name in the tier accent,
 * the benefit headline as the hero line, price + validity, then perks. The
 * merchant sees exactly what the customer will.
 */
const LiveCustomerCardPreview = ({ payload, storeName = 'Store name' }: Props) => {
  const theme = resolveThemeToken(payload.cardTheme ?? 'standard');
  const accent = theme.accentHex;
  const name = String(payload.name || '').trim() || 'Membership name';
  const perks = Array.isArray(payload.perks) ? payload.perks : [];
  const headline = benefitHeadline(payload);

  return (
    <div
      className="creator-preview-card creator-preview-card--offering"
      data-testid="creator-preview-card"
    >
      <span className="creator-preview-card__accent-line" style={{ background: theme.cardBackground }} />

      <header className="creator-preview-card__header">
        <span className="creator-preview-card__name" data-testid="creator-preview-card-name" style={{ color: accent }}>
          {name}
        </span>
        <span className="creator-preview-card__store">{storeName.toUpperCase()}</span>
      </header>

      <div className="creator-preview-card__hero" data-testid="creator-preview-card-headline">
        {headline}
      </div>

      <dl className="creator-preview-card__stats">
        <div>
          <dt>Price</dt>
          <dd data-testid="creator-preview-card-price">{formatPrice(payload.pricePaise)}</dd>
        </div>
        <div>
          <dt>Validity</dt>
          <dd data-testid="creator-preview-card-validity">{formatValidity(payload.validityDays)}</dd>
        </div>
      </dl>

      <ul className="creator-preview-card__perks">
        {perks.length > 0 ? (
          perks.map((perk, index) => (
            <li key={`${perk}-${index}`}>• {perk}</li>
          ))
        ) : (
          <li className="creator-preview-card__perks--placeholder">• Perks appear here (step 4)</li>
        )}
      </ul>
    </div>
  );
};

export default LiveCustomerCardPreview;
